import { useState } from "react";
import { flushSync } from "react-dom";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { printTools } from "@/lib/scroll";
import { STATUS, fmtDate } from "@/components/rc/shared";
import { toolkitStatus } from "@/components/rc/ToolkitsPanel";

const Th = ({ children }) => <th scope="col" className="border-b-2 border-line px-2 py-2 text-left text-sm font-bold">{children}</th>;
const Td = ({ children }) => <td className="border-b border-line px-2 py-2 align-top text-sm">{children}</td>;

const EhcpTable = ({ rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[560px] border-collapse" data-testid="report-ehcp-table">
      <thead><tr><Th>Pupil ref</Th><Th>Year</Th><Th>Need</Th><Th>Review due</Th><Th>Stage</Th><Th>Status</Th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="avoid-break">
            <Td><strong>{r.pupil_ref}</strong></Td><Td>{r.year_group || "—"}</Td><Td>{r.primary_need || "—"}</Td>
            <Td>{fmtDate(r.review_due)}</Td><Td>{r.stage}</Td>
            <Td><span className="flex items-center gap-1.5"><span className={`dot dot-${STATUS[r.status]?.tone}`} aria-hidden="true" />{STATUS[r.status]?.label}</span></Td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const H = ({ children }) => <h3 className="mb-3 mt-8 font-display text-2xl font-medium">{children}</h3>;

export const Report = ({ ws, data }) => {
  const [stamp, setStamp] = useState(null);
  const groups = toolkitStatus(data.saves);
  const count = (s) => data.ehcp.filter((r) => r.status === s).length;
  const exportPdf = async () => {
    try {
      const { data: d } = await api.post("/rc/audit/export", { report: "workspace-summary" });
      flushSync(() => setStamp(d));
      printTools("ws-report");
    } catch (err) {
      toast.error(errorText(err));
    }
  };
  return (
    <div className="print-scope mx-auto max-w-[1200px] px-5 pb-20 sm:px-8" data-testid="ws-report-wrap">
      <section id="ws-report" aria-labelledby="ws-report-title" className="tool-section scroll-mt-[150px] rounded-[2rem] border-2 border-line bg-surface p-5 sm:p-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow mb-2">Workspace report · {fmtDate(new Date().toISOString())}</p>
            <h2 id="ws-report-title" className="font-display text-3xl font-medium sm:text-4xl">{ws.org.name}: SEND summary</h2>
            <p className="mt-1 text-sm text-ink-soft">Prepared by {ws.user.name} · Workspace {ws.org.workspace_id}</p>
          </div>
          <button type="button" onClick={exportPdf} className="btn btn-brand no-print" data-testid="ws-report-download"><Download size={16} aria-hidden /> Download PDF report</button>
        </div>
        <H>EHCP annual reviews</H>
        <p className="mb-3 text-sm" data-testid="report-ehcp-summary">{data.ehcp.length} reviews tracked · {count("overdue")} overdue · {count("due_this_month")} due this month · {count("due_soon")} due in the next 60 days · {count("on_track")} on track</p>
        {data.ehcp.length ? <EhcpTable rows={data.ehcp} /> : <p className="text-ink-soft">No reviews recorded yet.</p>}
        <H>Toolkit progress</H>
        <div className="grid gap-6 sm:grid-cols-2">
          {groups.map((g) => (
            <div key={g.id} className="avoid-break">
              <p className="mb-2 font-bold">{g.title}: {g.items.filter((i) => i.saved).length} of {g.items.length} started</p>
              <ul className="space-y-1 text-sm">{g.items.map((i) => <li key={i.id}>{i.saved ? "✓" : "○"} {i.title}{i.saved ? ` (saved ${fmtDate(i.saved.updated_at)})` : ""}</li>)}</ul>
            </div>
          ))}
        </div>
        <H>Evidence log</H>
        {data.evidence.length ? (
          <ul className="space-y-1 text-sm" data-testid="report-evidence">{data.evidence.map((e) => <li key={e.id} className="avoid-break"><strong>{e.title}</strong> · {e.area || "General"} · {e.kind === "file" ? "Document" : "Note"} · {e.created_by}, {fmtDate(e.created_at)}</li>)}</ul>
        ) : <p className="text-ink-soft">No evidence added yet.</p>}
        <p className="mt-8 border-t border-line pt-4 text-xs text-ink-soft">This report contains pseudonymised information only (pupil reference codes). Keep the key that links codes to pupils securely within your school. The SEND Accountability Project Ltd.</p>
        {stamp && <p className="print-only-block report-watermark" data-testid="report-watermark">CONFIDENTIAL · {ws.org.name} · Exported by {stamp.exported_by} on {new Date(stamp.exported_at).toLocaleString("en-GB")}</p>}
      </section>
    </div>
  );
};
